// feedback/lib/deviceSnapshot.ts — privacy-critical client-side device
// snapshot for the submit panel (task spec §5: "no data collected" beyond
// this exact list, captured only on panel open, sent only inside the submit
// POST, never stored client-side, never sent anywhere else).
//
// HARD RULES enforced by this module's shape:
//  - Every read is a STANDARD browser API — nothing fingerprinting-adjacent
//    beyond what's listed below.
//  - A missing API degrades to an absent field, never an error (every read
//    here is individually try/caught or optional-chained).
//  - This module has no side effects and touches no storage — the caller
//    (SubmitFeedbackPanel) is the only thing that decides when to call it
//    (on panel open) and where the result goes (component state only, until
//    it rides along in the create() POST body if the reporter doesn't
//    exclude it).

import type { FeedbackDeviceSnapshot } from '@/core/models/feedback';

export interface ParsedUserAgent {
  devicePlatform: string;
  browser: string;
  osVersion: string;
}

/** Best-effort, dependency-free UA parse — good enough for a pre-filled,
 * fully editable form field, not a fingerprinting library. Every branch
 * degrades to an empty string rather than guessing wrong. */
export function parseUserAgent(ua: string): ParsedUserAgent {
  const source = ua || '';

  let devicePlatform = '';
  if (/windows/i.test(source)) devicePlatform = 'Windows';
  else if (/iphone|ipad|ipod/i.test(source)) devicePlatform = 'iOS';
  else if (/android/i.test(source)) devicePlatform = 'Android';
  else if (/mac os x|macintosh/i.test(source)) devicePlatform = 'macOS';
  else if (/linux/i.test(source)) devicePlatform = 'Linux';

  let browser = '';
  const edgeMatch = /Edg\/([\d.]+)/.exec(source);
  const chromeMatch = /Chrome\/([\d.]+)/.exec(source);
  const firefoxMatch = /Firefox\/([\d.]+)/.exec(source);
  const safariMatch = /Version\/([\d.]+).*Safari/.exec(source);
  if (edgeMatch) browser = `Edge ${edgeMatch[1]}`;
  else if (firefoxMatch) browser = `Firefox ${firefoxMatch[1]}`;
  else if (chromeMatch) browser = `Chrome ${chromeMatch[1]}`;
  else if (safariMatch) browser = `Safari ${safariMatch[1]}`;

  let osVersion = '';
  const winMatch = /Windows NT ([\d.]+)/.exec(source);
  const macMatch = /Mac OS X ([\d_.]+)/.exec(source);
  const androidMatch = /Android ([\d.]+)/.exec(source);
  const iosMatch = /OS ([\d_]+) like Mac OS X/.exec(source);
  if (winMatch) osVersion = winMatch[1] ?? '';
  else if (macMatch) osVersion = (macMatch[1] ?? '').replace(/_/g, '.');
  else if (androidMatch) osVersion = androidMatch[1] ?? '';
  else if (iosMatch) osVersion = (iosMatch[1] ?? '').replace(/_/g, '.');

  return { devicePlatform, browser, osVersion };
}

/** Approx RAM tier from navigator.deviceMemory (GB, may be undefined —
 * Firefox/Safari never expose it). Rendered as a human string ("8GB+")
 * rather than the raw float, since the API itself is already a coarse,
 * capped-at-8 approximation. */
function ramTier(): string | undefined {
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (typeof mem !== 'number' || Number.isNaN(mem)) return undefined;
  return mem >= 8 ? '8GB+' : `${mem}GB`;
}

/** GPU renderer string via the WEBGL_debug_renderer_info extension — may be
 * unavailable (extension disabled, no WebGL support, or a headless/locked-
 * down browser). Never throws: every step is defensively guarded. */
function gpuString(): string | undefined {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') ??
      canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return undefined;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) return undefined;
    const raw = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as unknown;
    return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
  } catch {
    return undefined;
  }
}

function connectionClass(): string | undefined {
  const conn = (
    navigator as Navigator & { connection?: { effectiveType?: string } }
  ).connection;
  return conn?.effectiveType || undefined;
}

/**
 * Captures the snapshot ONCE, synchronously, from standard browser APIs
 * only. Call this exactly at panel-open time (never on a timer, never
 * lazily re-read) — the caller holds the result in component state and it
 * lives nowhere else until (if) the reporter submits.
 */
export function captureDeviceSnapshot(): FeedbackDeviceSnapshot {
  const snapshot: FeedbackDeviceSnapshot = {};

  if (typeof navigator !== 'undefined') {
    if (typeof navigator.hardwareConcurrency === 'number') {
      snapshot.cpuCores = navigator.hardwareConcurrency;
    }
    const ram = ramTier();
    if (ram) snapshot.ramTier = ram;
    const conn = connectionClass();
    if (conn) snapshot.connection = conn;
  }

  if (typeof document !== 'undefined') {
    const gpu = gpuString();
    if (gpu) snapshot.gpu = gpu;
  }

  if (typeof screen !== 'undefined') {
    if (typeof screen.width === 'number') snapshot.screenWidth = screen.width;
    if (typeof screen.height === 'number') snapshot.screenHeight = screen.height;
  }
  if (typeof window !== 'undefined' && typeof window.devicePixelRatio === 'number') {
    snapshot.devicePixelRatio = window.devicePixelRatio;
  }

  return snapshot;
}

/** "GMT+8" / "GMT-5" / "GMT+0" style label from the local UTC offset — free
 * text, editable, matching submitted_tz's column shape on the backend. */
export function captureLocalTimezoneLabel(date: Date = new Date()): string {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  return `GMT${sign}${hours}`;
}

/** Human labels for the individually-deletable snapshot fields, in display order. */
export const DEVICE_SNAPSHOT_FIELD_LABELS: Record<keyof FeedbackDeviceSnapshot, string> = {
  cpuCores: 'CPU cores',
  ramTier: 'Approx. RAM',
  gpu: 'Graphics',
  screenWidth: 'Screen width',
  screenHeight: 'Screen height',
  devicePixelRatio: 'Pixel ratio',
  connection: 'Connection',
};

export const DEVICE_SNAPSHOT_FIELD_ORDER: (keyof FeedbackDeviceSnapshot)[] = [
  'cpuCores',
  'ramTier',
  'gpu',
  'screenWidth',
  'screenHeight',
  'devicePixelRatio',
  'connection',
];
